<template>
  <div class="login-container">
    <div class="login-form">
      <h1>管理員登入</h1>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <label for="database">選擇倉庫</label>
          <select
            id="database"
            class="form-control"
            v-model="selectedDatabase"
            required
          >
            <option
              v-for="db in availableDatabases"
              :key="db.key"
              :value="db.key"
            >
              {{ db.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="username">帳號</label>
          <input
            type="text"
            id="username"
            class="form-control"
            v-model="username"
            required
            autocomplete="off"
          />
        </div>

        <div class="form-group">
          <label for="password">密碼</label>
          <input
            type="password"
            id="password"
            class="form-control"
            v-model="password"
            required
            autocomplete="current-password"
          />
          <div class="password-options">
            <label class="remember-me">
              <input
                type="checkbox"
                v-model="rememberMe"
              />
              記住我
            </label>
          </div>
        </div>

        <button type="submit" class="btn login-btn" :disabled="isLoading">
          {{ isLoading ? '登入中...' : '登入' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/user'
import http from '../api/http'

const router = useRouter()
const userStore = useUserStore()

const username = ref('')
const password = ref('')
const selectedDatabase = ref('')
const availableDatabases = ref([])
const rememberMe = ref(false)
const errorMessage = ref('')
const isLoading = ref(false)

// 載入可用的倉庫列表
const loadAvailableDatabases = async () => {
  try {
    const response = await http.get('/api/admin/databases')
    
    if (response.data && response.data.success) {
      availableDatabases.value = response.data.databases
      // 預設選擇第一個倉庫（成舍）
      if (response.data.databases.length > 0) {
        selectedDatabase.value = response.data.databases[0].key
      }
    }
  } catch (error) {
    console.error('載入倉庫列表失敗:', error)
  }
}

// 舊版把密碼以 Base64 存在瀏覽器，改用續期票後一律清掉
const clearLegacyCredentials = () => {
  localStorage.removeItem('admin_remembered_credentials')
}

// 帶回上次登入的帳號與倉庫，密碼不保存
const loadRememberedAccount = () => {
  clearLegacyCredentials()

  try {
    const saved = localStorage.getItem('admin_refresh_session')
    if (!saved) return

    const { username: savedUsername, database: savedDatabase } = JSON.parse(saved)
    if (savedUsername) {
      username.value = savedUsername
      rememberMe.value = true
    }
    if (savedDatabase) {
      selectedDatabase.value = savedDatabase
    }
  } catch (error) {
    console.error('載入記住的帳號失敗:', error)
    localStorage.removeItem('admin_refresh_session')
  }
}

// 取消勾選時立即作廢本機的續期票
watch(rememberMe, (newValue) => {
  if (!newValue) {
    localStorage.removeItem('admin_refresh_session')
  }
})

async function handleLogin() {
  if (isLoading.value) return

  isLoading.value = true
  errorMessage.value = ''

  try {
    const { success, message } = await userStore.login({
      username: username.value,
      password: password.value,
      database: selectedDatabase.value,
      rememberMe: rememberMe.value
    })

    if (success) {
      router.push('/dashboard')
    } else {
      errorMessage.value = message
    }
  } catch (error) {
    errorMessage.value = '登入失敗，請稍後再試'
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

// 頁面載入時載入倉庫列表和記住的帳號密碼
onMounted(async () => {
  await loadAvailableDatabases()
  loadRememberedAccount()
})
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.login-form {
  width: 100%;
  max-width: 400px;
  padding: 30px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

h1 {
  margin-bottom: 24px;
  text-align: center;
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  background-color: white;
}

.form-control:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

select.form-control {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
}

.password-options {
  margin-top: 8px;
}

.remember-me {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

.remember-me input[type="checkbox"] {
  margin-right: 8px;
  cursor: pointer;
}

.login-btn {
  width: 100%;
  margin-top: 10px;
  padding: 12px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.login-btn:hover {
  background-color: #40a9ff;
}

.login-btn:disabled {
  background-color: #d9d9d9;
  cursor: not-allowed;
}

.error-message {
  padding: 10px;
  margin-bottom: 15px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  font-size: 14px;
}
</style>